import moment from 'moment';

/**
 * One place for how the admin renders an absolute timestamp, and in which
 * timezone — shared by the Reports Users table (Last Transaction), the user
 * detail panel and, per KAN-556, the Transactions table.
 *
 * Timezone decision (KAN-550 / KAN-556): timestamps render in the VIEWER'S
 * browser timezone and every screen that shows one states that zone once
 * (see `TIMEZONE_NOTE`). No timezone library is installed and the API returns
 * UTC ISO strings; the browser zone is what an admin reading "today at 3:12 PM"
 * expects, and labelling it removes the ambiguity the story complained about.
 * If the product later standardises on the institution's zone, change it here.
 */

export const DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm A';
export const DATE_FORMAT = 'MM/DD/YYYY';

/**
 * Seconds-precision variant (KAN-556's `MM/DD/YYYY - hh:mm:ss A`), rendered in
 * the SAME zone as `DATE_TIME_FORMAT` — the viewer's browser zone, stated once
 * per screen via `TIMEZONE_NOTE`.
 *
 * Not to be confused with `formatTimestampWithZone` below, which produces the
 * same literal format but renders in the zone the VALUE declares (KAN-610, for
 * the payroll-connection payloads that stamp an offset). Use this one for any
 * payload that is plain UTC, so a Jira `lastUpdatedAt` does not read as UTC on a
 * screen where every other timestamp is local.
 */
export const DATE_TIME_SECONDS_FORMAT = 'MM/DD/YYYY - hh:mm:ss A';

/**
 * The viewer's IANA zone (e.g. "America/New_York"), resolved ONCE at module
 * load: `Intl.DateTimeFormat` construction is not free and this used to run
 * per cell per render and per exported CSV row.
 */
export const DISPLAY_TIME_ZONE = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
    } catch (error) {
        return 'local';
    }
})();

const ABBREVIATION_FORMATTER = (() => {
    try {
        return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    } catch (error) {
        return null;
    }
})();

/**
 * Moment instance for a value, or `null` when it is empty or unparseable —
 * ONE validity check for callers that need both the label and, say, an ISO
 * title (never run `new Date(value)` on a value only moment validated:
 * Date's parser is stricter and throws on `.toISOString()`).
 *
 * @param {string | Date | import('moment').Moment | null | undefined} value
 * @returns {import('moment').Moment | null}
 */
export function parseDateTime(value) {
    if (value == null || value === '') return null;
    const m = moment(value);
    return m.isValid() ? m : null;
}

/**
 * Zone abbreviation IN EFFECT AT A GIVEN INSTANT (EST vs EDT). It must be
 * derived per value: a January timestamp viewed in July renders at EST wall
 * time, so labelling it with today's EDT would be wrong. Falls back to the
 * IANA zone when the engine cannot produce a short name.
 *
 * @param {string | Date | import('moment').Moment | null | undefined} [value] defaults to now
 * @returns {string}
 */
export function timeZoneAbbreviation(value = new Date()) {
    const m = parseDateTime(value);
    if (!m || !ABBREVIATION_FORMATTER) return DISPLAY_TIME_ZONE;
    try {
        const part = ABBREVIATION_FORMATTER.formatToParts(m.toDate()).find((p) => p.type === 'timeZoneName');
        return part?.value || DISPLAY_TIME_ZONE;
    } catch (error) {
        return DISPLAY_TIME_ZONE;
    }
}

/**
 * Screen-level note. Labelled with the IANA zone, not an abbreviation: the
 * abbreviation depends on the timestamp (DST), the zone does not.
 */
export const TIMEZONE_NOTE = `Times are shown in your browser's timezone (${DISPLAY_TIME_ZONE}).`;

/**
 * Format an ISO / Date / moment value as an absolute date + time.
 * Returns `null` when the value is empty or unparseable so callers decide
 * what "no value" means (never vs not loaded vs invalid) instead of getting a dash.
 *
 * @param {string | Date | import('moment').Moment | null | undefined} value
 * @param {string} [format=DATE_TIME_FORMAT]
 * @returns {string | null}
 */
export function formatDateTime(value, format = DATE_TIME_FORMAT) {
    const m = parseDateTime(value);
    return m ? m.format(format) : null;
}

// ---------------------------------------------------------------------------
// Timestamps rendered in the zone the value itself declares (KAN-610).
//
// The payroll-connection endpoints stamp every instant with the reporting
// zone's numeric offset and declare `timezone` in the payload, so these render
// the value as sent rather than converting to the browser zone the helpers
// above use. One caption says which zone that is.
// ---------------------------------------------------------------------------

export const REPORTING_TIMEZONE_LABEL = 'All times in Eastern Time (ET)';

/**
 * Caption for a payload that declares its zone; the fixed label when it does not.
 * @param {string | null | undefined} timezone IANA zone from the payload
 * @returns {string}
 */
export function formatReportingTimezoneCaption(timezone) {
    const zone = typeof timezone === 'string' ? timezone.trim() : '';
    return zone ? `All times in ${zone}` : REPORTING_TIMEZONE_LABEL;
}

function parseInOwnZone(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = moment.parseZone(value);
    return parsed.isValid() ? parsed : null;
}

/** `MM/DD/YYYY - hh:mm:ss A` in the value's own zone; an em dash when unparseable. */
export function formatTimestampWithZone(value) {
    const parsed = parseInOwnZone(value);
    return parsed ? parsed.format('MM/DD/YYYY - hh:mm:ss A') : '—';
}

/** "12 days ago" for the value; an em dash when unparseable. */
export function formatRelativeAge(value) {
    const parsed = parseInOwnZone(value);
    return parsed ? parsed.fromNow() : '—';
}

/** Absolute timestamp followed by its relative age: "08/21/2026 - 03:14:05 PM (12 days ago)". */
export function formatTimestampWithAge(value) {
    const parsed = parseInOwnZone(value);
    if (!parsed) {
        return '—';
    }
    return `${parsed.format('MM/DD/YYYY - hh:mm:ss A')} (${parsed.fromNow()})`;
}
